import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@app/ui';
import { FadeIn, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { BrandLink } from '../components/BrandLink';
import { RoadmapSection } from '../components/roadmap-section';
import { RouteErrorComponent } from '../components/route-error';
import { SiteFooter } from '../components/site-footer';
import { getServerSession } from '../lib/auth-server';

export const Route = createFileRoute('/')({
  beforeLoad: async () => {
    const session = await getServerSession();
    if (session) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: Landing,
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
});

function Landing() {
  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      {/* Hero */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-4 border-b">
        <BrandLink />
        <div className="flex gap-2 sm:gap-3">
          <Link to="/pricing">
            <Button variant="ghost" className="min-h-[44px] hidden sm:inline-flex">
              Pricing
            </Button>
          </Link>
          <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
            <Button variant="outline" className="min-h-[44px]">
              Sign In
            </Button>
          </Link>
          <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
            <Button className="min-h-[44px]">Get Started Free</Button>
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-24 px-6 text-center max-w-4xl mx-auto">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Built for everyone in the NIL economy
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              NilBoard is the deal manager for athletes, agents, and universities — plus a
              marketplace that connects brands to vetted talent. Every partnership, payment, and
              deadline in one place.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
                <Button size="lg">Start Tracking Deals</Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" size="lg">
                  See Pricing
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Free for athletes up to 5 deals. Agent, university, and marketplace plans on the
              pricing page.
            </p>
          </FadeIn>
        </section>

        {/* Pain Points */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-center mb-8">Sound Familiar?</h3>
            <StaggerContainer className="grid md:grid-cols-3 gap-6">
              <StaggerItem>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-lg font-medium mb-2">"Wait, did Nike pay me yet?"</p>
                    <p className="text-muted-foreground">
                      Scrolling through 3 months of DMs to find payment details. 1-3 hours wasted
                      every week.
                    </p>
                  </CardContent>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-lg font-medium mb-2">"I forgot about that post deadline"</p>
                    <p className="text-muted-foreground">
                      Missing deliverable deadlines because everything lives in screenshots and text
                      threads.
                    </p>
                  </CardContent>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-lg font-medium mb-2">"How much have I actually earned?"</p>
                    <p className="text-muted-foreground">
                      No way to see your total earnings, pending payments, or which brands still owe
                      you.
                    </p>
                  </CardContent>
                </Card>
              </StaggerItem>
            </StaggerContainer>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-center mb-8">
              Everything You Need to Manage NIL Deals
            </h3>
            <StaggerContainer className="grid md:grid-cols-2 gap-6">
              <StaggerItem>
                <Card>
                  <CardHeader>
                    <CardTitle>Screenshot-to-Deal AI</CardTitle>
                    <CardDescription>
                      Upload a DM screenshot. Our AI extracts brand, amount, deliverables, and
                      deadlines in seconds.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card>
                  <CardHeader>
                    <CardTitle>Payment Tracker</CardTitle>
                    <CardDescription>
                      See what's owed, what's received, and what's overdue. Get notified before
                      payments are late.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card>
                  <CardHeader>
                    <CardTitle>Earnings Dashboard</CardTitle>
                    <CardDescription>
                      Total earnings, pipeline value, and deal history at a glance. Export for taxes
                      anytime.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card>
                  <CardHeader>
                    <CardTitle>Deal Valuation</CardTitle>
                    <CardDescription>
                      Is that deal worth it? AI estimates fair market value based on your sport,
                      followers, and engagement.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card>
                  <CardHeader>
                    <CardTitle>Contract Summaries</CardTitle>
                    <CardDescription>
                      Upload a contract PDF. Get a plain-language summary with flagged issues before
                      you sign.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card>
                  <CardHeader>
                    <CardTitle>University Compliance</CardTitle>
                    <CardDescription>
                      University admins get aggregate reporting, athlete rosters, and compliance
                      dashboards.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </StaggerItem>
            </StaggerContainer>
          </div>
        </section>

        {/* Roadmap / Coming Soon */}
        <RoadmapSection />

        {/* Who NilBoard serves */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-5xl mx-auto">
            <h3 className="text-2xl font-bold text-center mb-2">Who NilBoard serves</h3>
            <p className="text-muted-foreground text-center mb-8">
              One platform. Three audiences. See how each uses NilBoard.
            </p>
            <StaggerContainer className="grid md:grid-cols-3 gap-6">
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Athletes</CardTitle>
                    <CardDescription>
                      Track every deal, payment, and deliverable. AI turns DM screenshots into
                      structured records in seconds.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to="/pricing" className="text-sm underline">
                      Athlete plans from free →
                    </Link>
                  </CardContent>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Agents</CardTitle>
                    <CardDescription>
                      A single dashboard for every athlete you represent. Pipeline, commissions,
                      and reporting in one place.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to="/pricing" className="text-sm underline">
                      Solo & team plans →
                    </Link>
                  </CardContent>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Universities</CardTitle>
                    <CardDescription>
                      Per-athlete compliance. Aggregate reporting, audit logs, and FERPA-safe
                      roster tools for your athletic department.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to="/pricing" className="text-sm underline">
                      Per-athlete pricing →
                    </Link>
                  </CardContent>
                </Card>
              </StaggerItem>
            </StaggerContainer>
          </div>
        </section>

        {/* How NilBoard makes money */}
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <h3 className="text-2xl font-bold text-center mb-2">How NilBoard makes money</h3>
            <p className="text-muted-foreground text-center mb-8">
              Three revenue engines, one flywheel — aligned with the people who use the platform.
            </p>
            <StaggerContainer className="grid md:grid-cols-3 gap-6">
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>1. Deal Manager Subscriptions</CardTitle>
                    <CardDescription>
                      Athletes, agents, and universities pay for unlimited deals, AI tools, and
                      compliance. Predictable, recurring, ~85–90% gross margin.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>2. Marketplace · Athlete → Brand</CardTitle>
                    <CardDescription>
                      When an athlete sources a deal through NilBoard, we take a small fee at
                      closing — well below the industry's 20% standard.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>3. Marketplace · Brand → Athlete</CardTitle>
                    <CardDescription>
                      When a brand discovers a vetted athlete here, we take a larger fee plus a
                      residual on repeat deals from the same brand.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </StaggerItem>
            </StaggerContainer>
            <p className="text-center text-sm text-muted-foreground mt-6">
              Fee schedules live on the{' '}
              <Link to="/pricing" className="underline">
                pricing page
              </Link>{' '}
              and in our{' '}
              <Link to="/terms" className="underline">
                terms
              </Link>
              .
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 sm:py-16 px-6 bg-primary text-primary-foreground text-center">
          <FadeIn whileInView>
            <div className="max-w-2xl mx-auto">
              <h3 className="text-2xl sm:text-3xl font-bold mb-4">
                Start Managing Your Deals in Under a Minute
              </h3>
              <p className="text-lg mb-6 opacity-90">
                Join college athletes who are taking control of their NIL earnings.
              </p>
              <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
                <Button size="lg" variant="secondary">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </FadeIn>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
